import React, { useState, useEffect, useRef } from "react";
import type { ReactElement, Dispatch, SetStateAction } from "react";
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  KeyboardArrowDown,
} from "@mui/icons-material";
import { ExternalLinkIcon } from "@ippon/shared-assets";
import { colors } from "@ippon/shared-components";
import {
  Box,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  List,
  Tooltip,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";

// Type definitions
export interface SidebarSubItem {
  to: string;
  label: string;
  onClick?: () => void;
  external?: boolean;
  icon?: React.ReactNode;
}

export interface SidebarMenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  subItems: SidebarSubItem[];
  onClick?: () => void;
  disabled?: boolean;
  hidden?: boolean;
  /** サブアイテムが1個でもサブメニューとして表示する */
  forceSubmenu?: boolean;
}

export interface LogoMenuItem {
  id: string;
  name: string;
  base_url: string;
}

export interface SidebarTheme {
  primaryColor?: string;
  hoverColor?: string;
  activeColor?: string;
  textColor?: string;
  backgroundColor?: string;
  borderColor?: string;
  width?: {
    open: number | string;
    closed: number | string;
  };
  transition?: string;
}

export interface SidebarProps {
  // Basic props
  sidebarOpen: boolean;
  setSidebarOpen: Dispatch<SetStateAction<boolean>>;

  // Menu configuration
  menus?: SidebarMenuItem[];

  // Logo customization
  logo?: React.ReactNode;
  logoCollapsed?: React.ReactNode;
  logoHeight?: number | string;
  logoMenuItems?: LogoMenuItem[];
  onLogoMenuItemClick?: (url: string) => void;

  // Navigation
  currentPath: string;
  onNavigate?: (path: string) => void;
  LinkComponent?: React.ComponentType<any>;

  // Appearance customization
  theme?: SidebarTheme;
  showToggleButton?: boolean;
  toggleButtonPosition?: { top?: number; right?: number };

  // Behavior customization
  autoCollapse?: boolean;
  autoCollapseWidth?: number;
  defaultOpenDropdowns?: string[];
  allowMultipleOpen?: boolean;

  // Custom styles
  containerStyle?: React.CSSProperties;
  menuItemStyle?: React.CSSProperties;

  // Callbacks
  onMenuItemClick?: (menuId: string, subItemTo?: string) => void;
  onToggle?: (isOpen: boolean) => void;
}

// Default theme
const defaultTheme: Required<SidebarTheme> = {
  primaryColor: "#0E9DBC",
  hoverColor: "#DBF8FF",
  activeColor: "#0e9dbc",
  textColor: "#354053",
  backgroundColor: "#FFFFFF",
  borderColor: "transparent",
  width: {
    open: "210px",
    closed: "60px",
  },
  transition: "0.2s",
};

// Merge themes
const mergeTheme = (custom?: SidebarTheme): Required<SidebarTheme> => ({
  ...defaultTheme,
  ...custom,
  width: {
    ...defaultTheme.width,
    ...custom?.width,
  },
});

export const Sidebar: React.FC<SidebarProps> = ({
  sidebarOpen,
  setSidebarOpen,
  menus = [],
  logo,
  logoCollapsed,
  logoHeight = 60,
  logoMenuItems = [],
  onLogoMenuItemClick,
  currentPath,
  onNavigate,
  LinkComponent,
  theme,
  showToggleButton = true,
  toggleButtonPosition = { top: 43, right: -15 },
  autoCollapse = false,
  autoCollapseWidth = 768,
  defaultOpenDropdowns = [],
  allowMultipleOpen = true,
  containerStyle,
  menuItemStyle,
  onMenuItemClick,
  onToggle,
}): ReactElement => {
  const [isHoveringToggle, setIsHoveringToggle] = useState(false);
  const [logoMenuAnchor, setLogoMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const mergedTheme = mergeTheme(theme);

  // Function to check if dropdown is active based on current path
  const isDropdownActive = (dropdownItems: SidebarSubItem[]) => {
    return dropdownItems.some(
      (item) =>
        currentPath === item.to ||
        currentPath.startsWith(`${item.to}/`) ||
        currentPath.match(new RegExp(`^${item.to}/[^/]+$`)),
    );
  };

  // Function to get initially active dropdowns based on current path
  const getInitialActiveDropdowns = () => {
    const activeDropdowns: string[] = [...defaultOpenDropdowns];
    menus.forEach((menu) => {
      if (isDropdownActive(menu.subItems)) {
        activeDropdowns.push(menu.id);
      }
    });
    return [...new Set(activeDropdowns)];
  };

  const [openDropdowns, setOpenDropdowns] = useState<string[]>(
    getInitialActiveDropdowns(),
  );
  const previousPathRef = useRef(currentPath);

  // Auto-collapse on small screens
  useEffect(() => {
    if (!autoCollapse) return;

    const handleResize = () => {
      if (window.innerWidth < autoCollapseWidth) {
        setSidebarOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [autoCollapse, autoCollapseWidth, setSidebarOpen]);

  // Update open dropdowns when current path changes
  useEffect(() => {
    if (previousPathRef.current === currentPath) {
      return;
    }
    const activeDropdowns = getInitialActiveDropdowns();
    requestAnimationFrame(() => {
      setOpenDropdowns((prevOpenDropdowns) => {
        const newDropdowns = allowMultipleOpen
          ? [...new Set([...prevOpenDropdowns, ...activeDropdowns])]
          : activeDropdowns;
        return newDropdowns;
      });
    });
    previousPathRef.current = currentPath;
  }, [currentPath, menus, allowMultipleOpen]);

  const handleDropdownToggle = (dropdownId: string) => {
    const selectedMenu = menus.find((menu) => menu.id === dropdownId);

    if (!selectedMenu || selectedMenu.disabled) return;

    // サブアイテムが1個かつforceSubmenuがfalseの場合は直接リンクとして扱う
    // ナビゲーションはLink（<a>タグ）経由で行うため、onNavigateは呼ばない
    // （CustomFormNavigationGuardがaタグのクリックを検知してブロックできるようにする）
    if (selectedMenu.subItems.length === 1 && !selectedMenu.forceSubmenu) {
      const onlyItem = selectedMenu.subItems[0];

      if (selectedMenu.onClick) {
        selectedMenu.onClick();
      }

      if (onMenuItemClick) {
        onMenuItemClick(dropdownId, onlyItem?.to);
      }

      return;
    }

    const firstItemPath = selectedMenu.subItems[0]?.to;

    if (!sidebarOpen) {
      if (firstItemPath && onNavigate) {
        onNavigate(firstItemPath);
      }
    } else {
      setOpenDropdowns((current) => {
        if (current.includes(dropdownId)) {
          return current.filter((id) => id !== dropdownId);
        } else {
          return allowMultipleOpen ? [...current, dropdownId] : [dropdownId];
        }
      });
    }

    if (selectedMenu.onClick) {
      selectedMenu.onClick();
    }

    if (onMenuItemClick) {
      onMenuItemClick(dropdownId);
    }
  };

  const handleSubItemClick = (menuId: string, subItem: SidebarSubItem) => {
    if (subItem.onClick) {
      subItem.onClick();
    }

    // 外部リンクの場合は新しいタブで開く
    if (subItem.external) {
      window.open(subItem.to, "_blank", "noopener,noreferrer");
    } else if (onNavigate) {
      onNavigate(subItem.to);
    }

    if (onMenuItemClick) {
      onMenuItemClick(menuId, subItem.to);
    }
  };

  const handleToggle = () => {
    const newState = !sidebarOpen;
    setSidebarOpen(newState);
    setTimeout(() => setIsHoveringToggle(false), 100);

    if (onToggle) {
      onToggle(newState);
    }
  };

  // Logo menu handlers
  const handleLogoClick = (event: React.MouseEvent<HTMLElement>) => {
    // Only open menu if there are logo menu items
    if (logoMenuItems.length === 0) {
      return;
    }
    setLogoMenuAnchor(event.currentTarget);
  };

  const handleLogoMenuClose = () => {
    setLogoMenuAnchor(null);
  };

  const handleExternalLink = (url: string) => {
    if (onLogoMenuItemClick) {
      onLogoMenuItemClick(url);
    }

    // 内部リンク（"/"で始まる）の場合は内部ナビゲーションを使用
    if (url.startsWith("/")) {
      if (onNavigate) {
        onNavigate(url);
      }
    } else {
      // 外部リンクの場合は新しいタブで開く
      window.open(url, "_blank", "noopener,noreferrer");
    }

    handleLogoMenuClose();
  };

  // Default Link component if none provided
  const Link =
    LinkComponent ||
    (({ to, children, style }: any) => (
      <a
        href={to}
        style={style}
        onClick={(e) => {
          e.preventDefault();
          if (onNavigate) {
            onNavigate(to);
          }
        }}
      >
        {children}
      </a>
    ));

  // Styles object with theme
  const styles = {
    container: (isOpen: boolean, isHovering: boolean) => ({
      bgcolor: mergedTheme.backgroundColor,
      height: "100%",
      position: "fixed" as const,
      width: isOpen ? "210px" : "60px",
      borderRight: isHovering
        ? `1px solid ${mergedTheme.primaryColor}`
        : `1px solid ${colors.border.tab}`,
      boxShadow: isHovering ? "0px 0px 10px rgba(0, 0, 0, 0.1)" : "none",
      transition: `width ${mergedTheme.transition} cubic-bezier(0.4, 0, 0.2, 1), border-right ${mergedTheme.transition}, box-shadow ${mergedTheme.transition}`,
      zIndex: 100,
      ...containerStyle,
    }),
    toggleButton: (isHovering: boolean) => ({
      position: "absolute" as const,
      top: toggleButtonPosition.top,
      right: toggleButtonPosition.right,
      width: 30,
      height: 30,
      background: "white",
      border: isHovering
        ? "1px solid #fff"
        : `1px solid ${mergedTheme.primaryColor}`,
      color: "#fff",
      opacity: isHovering ? 1 : 0,
      borderRadius: "50%",
      boxShadow: isHovering ? `0 0 0 1px ${mergedTheme.primaryColor}` : "none",
      transition: `all ${mergedTheme.transition}`,
      zIndex: 1000,
    }),
    menuItem: (
      isActive: boolean,
      isSingleLink: boolean,
      isMultiSubmenuParent: boolean,
    ) => ({
      margin: sidebarOpen ? "5px 15px" : "5px 12px",
      padding: sidebarOpen ? "6px 10px" : 0,
      borderRadius: "8px",
      backgroundColor: "transparent",
      width: sidebarOpen ? "calc(100% - 30px)" : "36px",
      height: sidebarOpen ? "auto" : "36px",
      display: "flex",
      justifyContent: sidebarOpen ? "flex-start" : "center",
      alignItems: "center",
      transition: `margin ${mergedTheme.transition} cubic-bezier(0.4, 0, 0.2, 1), padding ${mergedTheme.transition} cubic-bezier(0.4, 0, 0.2, 1), width ${mergedTheme.transition} cubic-bezier(0.4, 0, 0.2, 1), background-color ${mergedTheme.transition}`,
      overflow: "hidden",
      "& > .MuiListItemText-root": {
        overflow: "hidden",
      },
      "& > .MuiListItemIcon-root, & > .MuiListItemText-root span": {
        color: isActive ? mergedTheme.activeColor : mergedTheme.textColor,
        fontSize: "14px",
        fontWeight: isActive ? "bold" : "bold",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "clip",
      },
      "&:hover": {
        backgroundColor:
          isActive && isMultiSubmenuParent
            ? "#F5F5F5"
            : isActive
              ? mergedTheme.hoverColor
              : "#F5F5F5",
      },
      // 折りたたみ時は従来通り、アクティブなら背景色
      ...(isActive &&
        !sidebarOpen && { backgroundColor: mergedTheme.hoverColor }),
      // 展開時は subItem が1件のみのときに限って背景色
      ...(isActive &&
        sidebarOpen &&
        isSingleLink && { backgroundColor: mergedTheme.hoverColor }),
      ...menuItemStyle,
    }),
  };

  // Check if logo contains label (has multiple children)
  const hasLabel =
    React.isValidElement(logo) || React.isValidElement(logoCollapsed)
      ? (() => {
          const currentLogo = sidebarOpen ? logo : logoCollapsed;
          if (React.isValidElement(currentLogo)) {
            const elem = currentLogo as React.ReactElement<{
              children?: React.ReactNode;
            }>;
            const children = React.Children.toArray(elem.props?.children || []);
            // If logo has multiple children (logo image + label), it contains a label
            return children.length > 1;
          }
          return false;
        })()
      : false;

  // Filter out hidden menus
  const visibleMenus = menus.filter((menu) => !menu.hidden);

  return (
    <Box sx={styles.container(sidebarOpen, isHoveringToggle)}>
      {/* Logo Section */}
      <Box
        onClick={handleLogoClick}
        sx={{
          height: logoHeight,
          display: "flex",
          alignItems: "center",
          justifyContent: sidebarOpen ? "flex-start" : "center",
          cursor: logoMenuItems.length > 0 ? "pointer" : "default",
          "&:hover": {
            opacity: logoMenuItems.length > 0 ? 0.8 : 1,
          },
          px: sidebarOpen ? 2 : 0,
          transition: `padding ${mergedTheme.transition} cubic-bezier(0.4, 0, 0.2, 1)`,
        }}
      >
        {sidebarOpen
          ? logo || (
              <span
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: mergedTheme.primaryColor,
                }}
              >
                LOGO
              </span>
            )
          : logoCollapsed || (
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  color: mergedTheme.primaryColor,
                }}
              >
                L
              </span>
            )}
      </Box>

      {/* Logo menu dropdown */}
      <Menu
        anchorEl={logoMenuAnchor}
        open={Boolean(logoMenuAnchor)}
        onClose={handleLogoMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {logoMenuItems.map((item) => (
          <MenuItem
            key={item.id}
            onClick={() => handleExternalLink(item.base_url)}
            sx={{
              minWidth: 150,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2">{item.name}</Typography>
            <span style={{ marginLeft: "16px", display: "inline-flex" }}>
              <ExternalLinkIcon />
            </span>
          </MenuItem>
        ))}
      </Menu>

      {/* Toggle Button */}
      {showToggleButton && (
        <>
          <Box
            onMouseEnter={() => setIsHoveringToggle(true)}
            onMouseLeave={() => setIsHoveringToggle(false)}
            sx={{
              position: "absolute",
              top: 0,
              right: -10,
              width: 25,
              height: "100%",
              zIndex: 999,
            }}
          />
          <IconButton
            onClick={handleToggle}
            onMouseEnter={() => setIsHoveringToggle(true)}
            onMouseLeave={() => setIsHoveringToggle(false)}
            disableRipple
            sx={styles.toggleButton(isHoveringToggle)}
          >
            {sidebarOpen ? (
              <KeyboardArrowLeft sx={{ color: mergedTheme.primaryColor }} />
            ) : (
              <KeyboardArrowRight sx={{ color: mergedTheme.primaryColor }} />
            )}
          </IconButton>
        </>
      )}

      {/* Menu Items */}
      <List sx={{ mt: hasLabel ? 1 : 0 }}>
        {visibleMenus.map((menu) => {
          const isSingleLink = menu.subItems.length === 1 && !menu.forceSubmenu;
          const isMultiSubmenuParent =
            menu.subItems.length > 1 || Boolean(menu.forceSubmenu);
          const singleLinkTo = isSingleLink ? menu.subItems[0]?.to : undefined;
          const MenuItemButton = (
            <ListItemButton
              {...(isSingleLink && singleLinkTo
                ? { component: "div" as const, role: "presentation" as const }
                : {})}
              disableRipple
              disabled={menu.disabled}
              onClick={() => handleDropdownToggle(menu.id)}
              sx={styles.menuItem(
                isDropdownActive(menu.subItems),
                menu.subItems.length === 1,
                isMultiSubmenuParent,
              )}
            >
              {sidebarOpen ? (
                <>
                  <ListItemIcon
                    sx={{
                      minWidth: "20px",
                      padding: 0,
                      color: isDropdownActive(menu.subItems)
                        ? mergedTheme.activeColor
                        : "inherit",
                      "& svg": { width: "20px", height: "20px" },
                    }}
                  >
                    {menu.icon}
                  </ListItemIcon>
                  <ListItemText sx={{ ml: "10px" }} primary={menu.label} />
                  {(menu.subItems.length > 1 || menu.forceSubmenu) &&
                    (openDropdowns.includes(menu.id) ? (
                      <KeyboardArrowDown
                        sx={{
                          color: isDropdownActive(menu.subItems)
                            ? mergedTheme.activeColor
                            : "inherit",
                          pr: 1,
                        }}
                      />
                    ) : (
                      <KeyboardArrowRight
                        sx={{
                          color: isDropdownActive(menu.subItems)
                            ? mergedTheme.activeColor
                            : "inherit",
                          pr: 1,
                        }}
                      />
                    ))}
                </>
              ) : (
                <Tooltip title={menu.label} placement="right">
                  <ListItemIcon
                    sx={{
                      minWidth: "14px",
                      padding: "8px",
                      color: "inherit",
                      "& svg": { width: "20px", height: "20px" },
                    }}
                  >
                    {menu.icon}
                  </ListItemIcon>
                </Tooltip>
              )}
            </ListItemButton>
          );

          return (
            <Box key={menu.id}>
              {isSingleLink && singleLinkTo ? (
                <Link
                  to={singleLinkTo}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  {MenuItemButton}
                </Link>
              ) : (
                MenuItemButton
              )}

              {sidebarOpen &&
                (menu.subItems.length > 1 || menu.forceSubmenu) && (
                  <Collapse
                    in={openDropdowns.includes(menu.id)}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List
                      component="div"
                      disablePadding
                      sx={{
                        m: "0 8px",
                        p: 0,
                        backgroundColor: "transparent",
                        borderRadius: "0 0 8px 8px",
                        position: "relative",
                        pl: "8px",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          left: "27px",
                          top: 0,
                          bottom: 0,
                          width: "1px",
                          backgroundColor: "#e0e0e0",
                        },
                      }}
                    >
                      {menu.subItems.map((item) => {
                        const SubItemContent = (
                          <ListItemButton
                            disableRipple
                            onClick={(e) => {
                              if (item.external) {
                                e.preventDefault();
                              }
                              handleSubItemClick(menu.id, item);
                            }}
                            sx={{
                              padding: "6px 16px",
                              backgroundColor:
                                currentPath === item.to
                                  ? mergedTheme.hoverColor
                                  : "transparent",
                              borderRadius: 2,
                              ml: 3,
                              mt: 0.6,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              "& .MuiListItemText-root span": {
                                color:
                                  currentPath === item.to
                                    ? mergedTheme.activeColor
                                    : mergedTheme.textColor,
                                fontSize: "13px",
                                fontWeight:
                                  currentPath === item.to ? "bold" : "normal",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "clip",
                              },
                              "&:hover": {
                                backgroundColor:
                                  currentPath === item.to
                                    ? mergedTheme.hoverColor
                                    : "#F5F5F5",
                              },
                            }}
                          >
                            <ListItemText primary={item.label} />
                            {item.icon && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  color:
                                    currentPath === item.to
                                      ? mergedTheme.activeColor
                                      : "#666",
                                }}
                              >
                                {item.icon}
                              </Box>
                            )}
                          </ListItemButton>
                        );

                        return item.external ? (
                          <Box
                            key={item.to}
                            sx={{ textDecoration: "none", color: "inherit" }}
                          >
                            {SubItemContent}
                          </Box>
                        ) : (
                          <Link
                            key={item.to}
                            to={item.to}
                            style={{ textDecoration: "none", color: "inherit" }}
                          >
                            {SubItemContent}
                          </Link>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
            </Box>
          );
        })}
      </List>
    </Box>
  );
};
